import { useState, useEffect, useCallback } from 'react';
import { Hub, HubCallback, HubCapsule } from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';
import {
  AmplifyAuthEvent,
  Consent,
  GandalfUser,
  MigrationStatus,
} from '../types/auth';
import { CustomCognitoUser } from '../types/amplifyAuthExtensions';
import UnauthorizedError from '../errors/UnauthorizedError';
import { useStateStorage } from '../contexts/StateStorageContextProvider';
import MissingEmailError from '../errors/MissingEmailError';
import { getAmplifyConfig } from '../components/postAuth/ValidateAccountLink';
import { AppConfig } from '../types/app';
import {
  DEFAULT_EULA_ATTRIBUTE_NAME,
  MERGE_TYPE_APN_DEPRECATION,
} from '../constants/auth';
import setTokens from '../services/setTokens';
import { includesEmail } from '../utils/email';
import { isPreTokenGenerationForbiddenError } from '../utils/errorHelper';

let initAccountLinkAmplifyConfig: any;

const getVibeId = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.vibe_user_id;
};

const getHatId = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.hat_id;
};

const getName = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.name;
};

const getProviderName = (user: CustomCognitoUser) => {
  // We only expect to ever find one identity in the array which is why we use the item.
  return user?.signInUserSession?.idToken?.payload?.public_provider_name;
};

const getIdentities = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.identities;
};

const getEulaStatus = (
  user: CustomCognitoUser,
  eulaAcceptanceAttribute: string
) => {
  return (user?.signInUserSession?.idToken?.payload as Record<string, any>)[
    eulaAcceptanceAttribute
  ] as string | undefined;
};

const getJwtToken = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.jwtToken;
};

const getEmail = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.email;
};

const getNewUser = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.new_user;
};

const getAssociate_to = (user: CustomCognitoUser) => {
  const associateTo = user?.signInUserSession?.idToken?.payload?.associate_to;
  return associateTo ? JSON.parse(associateTo) : undefined;
};

const getInvation = (user: CustomCognitoUser) => {
  const invitation = user?.signInUserSession?.idToken?.payload?.invitation;
  return invitation ? JSON.parse(invitation) : undefined;
};

const getFailedAssociate = (user: CustomCognitoUser) => {
  const failedAssociate =
    user?.signInUserSession?.idToken?.payload?.failed_associate;
  return failedAssociate ? JSON.parse(failedAssociate) : undefined;
};

const getCreatedOn = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.created_on;
};

const getEmailVerified = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.email_verified;
};

const getEmailVerificationRequiredFlag = (user: CustomCognitoUser) => {
  return (
    user?.signInUserSession?.idToken?.payload?.email_verification_required ===
    'true'
  );
};

const getOrganizationDetails = (user: CustomCognitoUser) => {
  const orgDetails = user?.signInUserSession?.idToken?.payload?.organization;
  return orgDetails ? JSON.parse(orgDetails) : undefined;
};

const getProfileDeactivatedFlag = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.deactivated === 'true';
};

const getSignInSwitchNeeded = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.signin_switch_needed;
};

const getProfileCompletionStatus = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.profile_completion_status;
};

const getSimplifiedProfileCompleted = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload
    ?.simplified_profile_completed;
};

const getSuspendedAccount = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload?.suspended;
};

const getMigrationStatus = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload
    ?.migration_status as MigrationStatus;
};

const getAutoLinkConsent = (user: CustomCognitoUser): Consent | undefined => {
  const payload = user?.signInUserSession?.idToken?.payload;
  const reshellifiedMerge = payload?.reshellified_merge;
  if (reshellifiedMerge) {
    return JSON.parse(reshellifiedMerge);
  }
  const apnAutoLinked = payload?.apn_auto_linked;
  if (apnAutoLinked) {
    return {
      ...JSON.parse(apnAutoLinked),
      merge_type: MERGE_TYPE_APN_DEPRECATION,
    };
  }
  return undefined;
};

const getSignInSwitchNeededB2I = (user: CustomCognitoUser) => {
  return user?.signInUserSession?.idToken?.payload
    ?.signin_switch_needed_for_b2i;
};

const useHubListener = (channel: string, callback: HubCallback) => {
  useEffect(() => {
    if (initAccountLinkAmplifyConfig) {
      Auth.configure(initAccountLinkAmplifyConfig.Auth);
    }
    Hub.listen(channel, callback);
    return () => {
      Hub.remove(channel, callback);
    };
  }, [channel, callback]);
};

export const accountLinkingInit = (
  configureAuthAccountLink: boolean,
  config: AppConfig
) => {
  if (configureAuthAccountLink === true && config) {
    const amplifyConfig = getAmplifyConfig(config);
    initAccountLinkAmplifyConfig = amplifyConfig;
    Auth.configure(amplifyConfig.Auth);
  }
};

export function useGandalfPostAuth(
  configureAuthAccountLink?: boolean,
  config?: AppConfig
) {
  if (configureAuthAccountLink === true && config) {
    const amplifyConfig = getAmplifyConfig(config);
    Auth.configure(amplifyConfig.Auth);
  }
  const [user, setUser] = useState<GandalfUser | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [customState, setCustomState] = useState<string | undefined>();
  const [receivedNonce, setReceivedAuthNonce] = useState<string | undefined>();
  const [tokenExpirationTime, setTokenExpirationTime] = useState<
    number | undefined
  >();
  const stateStorage = useStateStorage();
  const getUser = useCallback(async () => {
    const userData = await Auth.currentAuthenticatedUser();
    const customCognitoUserData = userData as CustomCognitoUser;
    // Get the session information
    setTokenExpirationTime(
      customCognitoUserData.signInUserSession?.idToken?.payload?.exp
    );
    if (config?.setTokensApiPath) {
      await setTokens(config.setTokensApiPath, {
        idToken: customCognitoUserData.signInUserSession
          .getIdToken()
          .getJwtToken(),
        accessToken: customCognitoUserData.signInUserSession
          .getAccessToken()
          .getJwtToken(),
        refreshToken: customCognitoUserData.signInUserSession
          .getRefreshToken()
          .getToken(),
      });
    }
    setUser({
      vibeId: getVibeId(userData),
      hatId: getHatId(userData),
      providerName: getProviderName(userData),
      name: getName(userData),
      eulaStatus:
        (config?.eulaAcceptanceAttributeName
          ? getEulaStatus(userData, config?.eulaAcceptanceAttributeName)
          : undefined) ||
        getEulaStatus(userData, DEFAULT_EULA_ATTRIBUTE_NAME) ||
        'false',
      jwtToken: getJwtToken(userData),
      email: getEmail(userData),
      associate_to: getAssociate_to(userData),
      failed_associate: getFailedAssociate(userData),
      new_user: getNewUser(userData),
      created_on: getCreatedOn(userData),
      email_verified: getEmailVerified(userData),
      invitation: getInvation(userData),
      email_verification_required: getEmailVerificationRequiredFlag(userData),
      organization: getOrganizationDetails(userData),
      deactivated: getProfileDeactivatedFlag(userData),
      signin_switch_needed: getSignInSwitchNeeded(userData),
      profile_completion_status: getProfileCompletionStatus(userData),
      simplified_profile_completed: getSimplifiedProfileCompleted(userData),
      suspended: getSuspendedAccount(userData),
      identities: getIdentities(userData),
      migration_status: getMigrationStatus(userData),
      autoLinkConsent: getAutoLinkConsent(userData),
      signin_switch_needed_b2i: getSignInSwitchNeededB2I(userData),
    });
  }, [config]);
  const handleAuthEvent = useCallback(
    ({ payload: { event, data } }: HubCapsule) => {
      switch (event) {
        case AmplifyAuthEvent.signIn:
        case AmplifyAuthEvent.cognitoHostedUI:
          getUser().catch((error) => {
            setError(error);
            // eslint-disable-next-line no-console
            console.debug(
              'Get user failed after getting signIn Hub event.',
              data
            );
          });
          break;
        case AmplifyAuthEvent.customOAuthState:
          try {
            setReceivedAuthNonce(data);
            const value = stateStorage.getItem(data);
            stateStorage.removeItem(data);
            setCustomState(value ?? undefined);
          } catch (signInError) {
            // eslint-disable-next-line no-console
            console.error(
              `Error setting the OAuth state ${(signInError as Error).message}`
            );
            setError(signInError as Error);
          }
          break;
        case AmplifyAuthEvent.signOut:
          setUser(null);
          break;
        case AmplifyAuthEvent.signIn_failure:
        case AmplifyAuthEvent.cognitoHostedUI_failure:
        case AmplifyAuthEvent.customState_failure:
          const errorMsg = data?.message || 'Unknown error.';
          if (isPreTokenGenerationForbiddenError(errorMsg)) {
            if (!includesEmail(errorMsg)) {
              setError(new MissingEmailError(errorMsg));
            } else {
              setError(new UnauthorizedError(errorMsg));
            }
          } else {
            setError(new Error(errorMsg));
            // eslint-disable-next-line no-console
            console.error(
              `Error unknown sign in failure ${(errorMsg as Error).message}`
            );
          }
          // eslint-disable-next-line no-console
          console.debug('Sign in failed: ', data);
          break;
        default:
          break;
      }
    },
    [getUser, stateStorage]
  );

  useHubListener('auth', handleAuthEvent);
  return {
    user,
    error,
    customState,
    receivedNonce,
    tokenExpirationTime,
  };
}

export default useGandalfPostAuth;
